import Header from '../../components/Header';
import Footer from '../../components/Footer';
import { ArrowLeft, Gift, AlertTriangle, TrendingUp } from 'lucide-react';
import { Link } from 'react-router-dom';

const ComprendreBonusBienvenue = () => {
  return (
    <div className="min-h-screen bg-dark-bg">
      <Header />
      <main className="py-20">
        <div className="container mx-auto px-4 max-w-4xl">
          <Link
            to="/guides"
            data-discover="true"
            className="inline-flex items-center text-toxic-green hover:text-opacity-80 mb-8 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour aux guides
          </Link>

          <article className="bg-dark-card border-2 border-gray-800 rounded-2xl p-8 md:p-12">
            <h1 className="text-4xl md:text-5xl font-bold mb-6 text-white">
              Comprendre les Bonus de Bienvenue
            </h1>

            <div className="prose prose-invert max-w-none">
              <p className="text-xl text-gray-300 mb-8 leading-relaxed">
                Les bonus de bienvenue sont partout dans le monde des paris en ligne. "100€ offerts", "200€ + 50€ de paris gratuits", "Doublez votre premier dépôt" - ces promesses attirent l'œil, mais que signifient-elles vraiment ? Ce guide vous explique tout ce que vous devez savoir pour tirer le maximum de ces offres sans tomber dans les pièges.
              </p>

              <h2 className="text-3xl font-bold mt-12 mb-6 text-white">
                Les Différents Types de Bonus
              </h2>
              <p className="text-gray-300 mb-6 leading-relaxed">
                Tous les bonus ne fonctionnent pas de la même manière. Le bonus de dépôt correspond à un pourcentage de votre premier dépôt : si vous déposez 100€ et que le bonus est de 100%, vous recevez 100€ supplémentaires. Le bonus fixe vous donne un montant déterminé quel que soit votre dépôt, souvent avec un minimum requis. Les paris gratuits sont des mises offertes que vous pouvez utiliser sans risquer votre propre argent.
              </p>
              <p className="text-gray-300 mb-6 leading-relaxed">
                Certaines plateformes combinent plusieurs types de bonus. Par exemple, un bonus de dépôt de 100% jusqu'à 200€, plus 50€ de paris gratuits après validation du compte. Ces offres combinées peuvent être très avantageuses, mais elles ont aussi des conditions plus complexes à remplir.
              </p>

              <h2 className="text-3xl font-bold mt-12 mb-6 text-white">
                Les Conditions de Mise : Le Piège à Éviter
              </h2>
              <p className="text-gray-300 mb-6 leading-relaxed">
                C'est ici que la plupart des parieurs se font avoir. Les conditions de mise (wagering requirements) déterminent combien de fois vous devez miser le montant du bonus avant de pouvoir retirer vos gains. Si vous recevez 100€ de bonus avec des conditions x5, vous devrez miser 500€ au total avant de pouvoir retirer quoi que ce soit.
              </p>
              <p className="text-gray-300 mb-6 leading-relaxed">
                Ces conditions s'appliquent généralement à la fois au bonus et à votre dépôt initial. Si vous déposez 100€ et recevez 100€ de bonus avec des conditions x5, vous devrez miser 1000€ (500€ pour le bonus + 500€ pour le dépôt). C'est beaucoup plus que ce que beaucoup de parieurs imaginent.
              </p>
              <p className="text-gray-300 mb-6 leading-relaxed">
                Certaines plateformes imposent aussi des limites sur les types de paris éligibles. Les paris à cotes très élevées (supérieures à 2.0 ou 3.0) peuvent ne compter que partiellement ou pas du tout dans les conditions de mise. Vérifiez toujours ces restrictions avant d'accepter un bonus.
              </p>

              <h2 className="text-3xl font-bold mt-12 mb-6 text-white">
                Calculer la Valeur Réelle d'un Bonus
              </h2>
              <p className="text-gray-300 mb-6 leading-relaxed">
                Un bonus de 200€ n'est pas toujours meilleur qu'un bonus de 100€. Pour comparer objectivement, calculez le montant total que vous devrez miser pour libérer le bonus, puis estimez vos chances de réussir. Si vous avez 1000€ à miser pour libérer 200€ de bonus, mais que vous perdez 150€ en cours de route, votre gain net n'est que de 50€.
              </p>
              <p className="text-gray-300 mb-6 leading-relaxed">
                Tenez compte aussi des délais. Certains bonus expirent après 30 jours si les conditions ne sont pas remplies. Si vous ne pariez pas régulièrement, un bonus avec des conditions élevées peut être impossible à libérer dans les temps. Préférez alors un bonus plus modeste mais avec des conditions réalistes.
              </p>

              <h2 className="text-3xl font-bold mt-12 mb-6 text-white">
                Les Restrictions Cachées
              </h2>
              <p className="text-gray-300 mb-6 leading-relaxed">
                Lisez toujours les conditions générales dans leur intégralité. Certaines plateformes limitent les montants maximum que vous pouvez retirer avec un bonus, même si vous avez rempli toutes les conditions. D'autres imposent des limites sur les sports ou les types de paris éligibles.
              </p>
              <p className="text-gray-300 mb-6 leading-relaxed">
                Les paris combinés peuvent être exclus ou ne compter que partiellement. Si vous aimez les paris multiples, vérifiez que votre stratégie est compatible avec les conditions du bonus. Certains bonus ne peuvent être utilisés que sur des paris simples, ce qui limite considérablement vos options.
              </p>

              <h2 className="text-3xl font-bold mt-12 mb-6 text-white">
                Stratégies pour Maximiser les Bonus
              </h2>
              <p className="text-gray-300 mb-6 leading-relaxed">
                Pour tirer le maximum d'un bonus, misez sur des événements avec des cotes modérées (entre 1.5 et 2.5). Ces paris offrent un bon équilibre entre sécurité et rendement. Évitez les cotes très élevées qui peuvent ne pas compter dans les conditions, et les cotes très basses qui rapportent peu.
              </p>
              <p className="text-gray-300 mb-6 leading-relaxed">
                Répartissez vos mises sur plusieurs événements plutôt que de tout miser sur un seul. Cela réduit le risque de tout perdre d'un coup et vous permet de progresser régulièrement vers l'objectif des conditions de mise.
              </p>
              <p className="text-gray-300 mb-6 leading-relaxed">
                Si vous recevez des paris gratuits, utilisez-les sur des événements avec des cotes élevées. Comme vous ne risquez pas votre propre argent, vous pouvez vous permettre de viser des gains importants. Même si vous perdez, vous n'avez rien perdu.
              </p>

              <h2 className="text-3xl font-bold mt-12 mb-6 text-white">
                Quand Refuser un Bonus
              </h2>
              <p className="text-gray-300 mb-6 leading-relaxed">
                Parfois, il vaut mieux refuser un bonus. Si vous êtes un parieur expérimenté avec une stratégie qui fonctionne, les restrictions d'un bonus peuvent limiter votre capacité à appliquer cette stratégie. Les conditions de mise peuvent vous forcer à parier sur des événements que vous n'auriez pas choisis autrement.
              </p>
              <p className="text-gray-300 mb-6 leading-relaxed">
                Si vous prévoyez de retirer rapidement après quelques paris gagnants, un bonus peut être un obstacle. Vous devrez d'abord remplir les conditions avant de pouvoir retirer, ce qui peut prendre du temps et vous exposer à des risques supplémentaires.
              </p>

              <div className="bg-toxic-green/10 border border-toxic-green/30 rounded-xl p-6 mt-12">
                <h3 className="text-2xl font-bold mb-4 text-white flex items-center">
                  <Gift className="w-6 h-6 text-toxic-green mr-3" />
                  Conseils Essentiels
                </h3>
                <ul className="space-y-3 text-gray-300">
                  <li className="flex items-start">
                    <span className="text-toxic-green mr-2">•</span>
                    <span>Lisez toujours les conditions générales avant d'accepter un bonus</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-toxic-green mr-2">•</span>
                    <span>Calculez le montant total à miser avant de vous engager</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-toxic-green mr-2">•</span>
                    <span>Vérifiez les restrictions sur les types de paris et les cotes</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-toxic-green mr-2">•</span>
                    <span>Un bonus plus modeste avec de meilleures conditions peut être plus avantageux</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-toxic-green mr-2">•</span>
                    <span>N'hésitez pas à refuser un bonus si les conditions ne vous conviennent pas</span>
                  </li>
                </ul>
              </div>
            </div>
          </article>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default ComprendreBonusBienvenue;

